import { CalculationRowUnit, HourlySalaryWithWorkingTimeCompensationUsecase, UserDefinedRow, WorkingTimeCompensationKind, WorkingTimeCompensationPricing } from "../../model";
import { IncomeTypeMetadata, IncomeTypesLogic, Translations } from "../../logic";
import { Numeric } from "../../util";
import { UsecaseValidationMessage } from "../UsecaseValidationMessage";
import { BaseUsecaseLogic } from "../BaseUsecaseLogic";

/** Client-side logic for HourlySalaryWithWorkingTimeCompensationUsecaseLogic  */
export class HourlySalaryWithWorkingTimeCompensationUsecaseLogic extends BaseUsecaseLogic {

  /**
   * Updates the usecase values based on user input without going to the server.
   * @param row Row to update.
   */
  public updateUsecase(row: UserDefinedRow): void {
    const data: HourlySalaryWithWorkingTimeCompensationUsecase = row.data ?? {};

    if (!data.compensationRows || data.compensationRows.length < 1) {
      data.compensationRows = [{}];
    }
    this.harmonize(row);
    for (const cr of data.compensationRows) {
      switch (data.pricing) {
        case WorkingTimeCompensationPricing.Percent:
            cr.price = Numeric.round((cr.percent ?? 0) * (row.price ?? 0), 2);
            break;
        case WorkingTimeCompensationPricing.Fixed:
            // cr.price = cr.price;
            break;
        default:
            // never
            break;
      }
    }
  }

  /** Gets validation message */
  public getUcValidation(row: UserDefinedRow): UsecaseValidationMessage {
    const data: HourlySalaryWithWorkingTimeCompensationUsecase = row.data || {};

    if (!data.compensationRows || data.compensationRows.length < 1) {
      return;
    }

    this.harmonize(row);
    const codeData = this.getCodeData(data.kind);
    if (!codeData) {
      return {
        type: "error",
        msg: "SALAXY.VALIDATION.Usecases.compensation.noCompensationData",
      };
    }
    const msgs = [];
    for (const cr of data.compensationRows) {
      const rowMsg = data.pricing === WorkingTimeCompensationPricing.Percent ?
        `${Translations.get("SALAXY.VALIDATION.Usecases.compensation.compensation")} ${Numeric.formatPercent((cr.percent ?? 0) * 100)}, ${Numeric.formatPrice(cr.price)} ${Translations.get("SALAXY.VALIDATION.Usecases.compensation.compensation2")} ${Numeric.formatPrice(Numeric.round((cr.count ?? row.count ?? 1) * (cr.price ?? 0), 2))}` :
        `${Translations.get("SALAXY.VALIDATION.Usecases.compensation.compensation")} ${Numeric.formatPrice(cr.price)} ${Numeric.formatPrice(cr.price)} ${Translations.get("SALAXY.VALIDATION.Usecases.compensation.compensation2")} ${Numeric.formatPrice(Numeric.round((cr.count ?? row.count ?? 1) * (cr.price ?? 0), 2))}`;
      msgs.push(rowMsg);
    }
    return { type: "default", msg: msgs.join("\n") };
  }

  private getCodeData(kind: WorkingTimeCompensationKind): IncomeTypeMetadata {
    if (kind) {
      let code = null;
      switch (kind) {
        case WorkingTimeCompensationKind.EmergencyWorkCompensation:
          code = 205;
          break;
        case WorkingTimeCompensationKind.EveningWorkCompensation:
          code = 206;
          break;
        case WorkingTimeCompensationKind.EveningShiftAllowance:
          code = 207;
          break;
        case WorkingTimeCompensationKind.SaturdayPay:
          code = 211;
          break;
        case WorkingTimeCompensationKind.ExtraWorkPremium:
          code = 212;
          break;
        case WorkingTimeCompensationKind.OtherCompensation:
          code = 216;
          break;
        case WorkingTimeCompensationKind.WaitingTimeCompensation:
          code = 217;
          break;
        case WorkingTimeCompensationKind.SundayWorkCompensation:
          code = 221;
          break;
        case WorkingTimeCompensationKind.StandByCompensation:
          code = 230;
          break;
        case WorkingTimeCompensationKind.WeeklyRestCompensation:
          code = 232;
          break;
        case WorkingTimeCompensationKind.OvertimeCompensation:
          code = 235;
          break;
        case WorkingTimeCompensationKind.NightWorkAllowance:
          code = 236;
          break;
        case WorkingTimeCompensationKind.NightShiftCompensation:
          code = 237;
          break;
        case WorkingTimeCompensationKind.Undefined:
        default:
          code = 0;
          break;
      }
      return IncomeTypesLogic.getAll().find((x) => x.code === code);
    }
    return null;
  }

  private harmonize(row: UserDefinedRow) {
    const data: HourlySalaryWithWorkingTimeCompensationUsecase = row?.data || {};
    for (const cr of data.compensationRows) {
      // legacy support
      if (data.pricing == null || data.pricing == WorkingTimeCompensationPricing.Undefined)
        {
            if (!cr.unit || cr.unit == CalculationRowUnit.Undefined || cr.unit == CalculationRowUnit.Percent)
            {
                data.pricing = WorkingTimeCompensationPricing.Percent;
                cr.percent = cr.count;
                cr.count = null;
            }
            else
            {
                data.pricing = WorkingTimeCompensationPricing.Fixed;
                cr.percent = null;
                cr.count = null;
            }
        }
    }
  }
}
